<template>
    <BaseTemplate app-name="" page-title="">
        <template #content>
            <div class="flex h-screen bg-gray-50 dark:bg-gray-900">
                <!-- Mobile Overlay -->
                <div 
                    v-if="sidebarOpen" 
                    class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden dark:bg-opacity-80 dark:bg-gray-900"
                    @click="sidebarOpen = false"
                ></div>

                <!-- Sidebar -->
                <div 
                    :class="[
                        'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
                        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    ]"
                >
                    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                                    <Headset class="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 class="font-semibold text-gray-900 dark:text-gray-100">HelpDesk TI</h2>
                                    <p class="text-sm text-gray-500 dark:text-gray-400">Sistema de Chamados</p>
                                </div>
                            </div>
                            <!-- Close button for mobile -->
                            <button 
                                @click="sidebarOpen = false"
                                class="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <X class="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    
                    <nav class="p-4 space-y-2">
                        <a href="#" class="flex items-center space-x-3 px-3 py-2 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg">
                            <LayoutDashboard class="w-5 h-5" />
                            <span class="font-medium">Dashboard</span>
                        </a>
                        <a href="#" class="flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <Ticket class="w-5 h-5" />
                            <span>Todos os Chamados</span>
                        </a>
                        <label class="flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <input type="checkbox" value="pendente" class="rounded text-orange-500 focus:ring-orange-500" />
                            <Clock class="w-5 h-5" />
                            <span>Pendentes</span>
                        </label>
                        <label class="flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <input type="checkbox" value="urgente" class="rounded text-red-500 focus:ring-red-500" />
                            <AlertCircle class="w-5 h-5" />
                            <span>Urgentes</span>
                        </label>
                        <label class="flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <input type="checkbox" value="resolvido" class="rounded text-green-500 focus:ring-green-500" />
                            <CheckCircle class="w-5 h-5" />
                            <span>Resolvidos</span>
                        </label>
                        <a href="#" class="flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                            <Users class="w-5 h-5" />
                            <span>Meus Chamados</span>
                        </a>
                    </nav>
                </div>

                <!-- Main Content -->
                <div class="flex-1 flex flex-col overflow-hidden lg:ml-0">
                    <!-- Mobile Header -->
                    <div class="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                        <div class="flex items-center justify-between">
                            <button 
                                @click="sidebarOpen = true"
                                class="p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <Menu class="w-6 h-6" />
                            </button>
                            <h1 class="text-lg font-semibold text-gray-900 dark:text-gray-100">HelpDesk TI</h1>
                            <button class="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700">
                                <Plus class="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <!-- Stats Cards -->
                    <div class="p-4 lg:p-6 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-6">
                            <div class="flex items-center">
                                <div class="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                    <Ticket class="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                                </div>
                                <div class="ml-3 lg:ml-4">
                                    <p class="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-300">Total</p>
                                    <p class="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">24</p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-6">
                            <div class="flex items-center">
                                <div class="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                                    <Clock class="w-5 h-5 lg:w-6 lg:h-6 text-orange-600" />
                                </div>
                                <div class="ml-3 lg:ml-4">
                                    <p class="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-300">Pendentes</p>
                                    <p class="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">8</p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-6">
                            <div class="flex items-center">
                                <div class="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                                    <AlertCircle class="w-5 h-5 lg:w-6 lg:h-6 text-red-600" />
                                </div>
                                <div class="ml-3 lg:ml-4">
                                    <p class="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-300">Urgentes</p>
                                    <p class="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">3</p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 lg:p-6">
                            <div class="flex items-center">
                                <div class="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                                    <CheckCircle class="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
                                </div>
                                <div class="ml-3 lg:ml-4">
                                    <p class="text-xs lg:text-sm font-medium text-gray-600 dark:text-gray-300">Resolvidos</p>
                                    <p class="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">13</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Filters and Search -->
                    <div class="px-4 lg:px-6 pb-4">
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                            <div class="space-y-4">
                                <!-- Search Bar -->
                                <div class="relative">
                                    <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300 w-4 h-4" />
                                    <input 
                                        type="text" 
                                        placeholder="Buscar chamados..." 
                                        class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                        v-model="searchQuery"
                                    />
                                </div>
                                
                                <!-- Filters Row -->
                                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
                                    <div class="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 flex-1">
                                        <select class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" v-model="statusFilter">
                                            <option value="">Todos os Status</option>
                                            <option value="aberto">Aberto</option>
                                            <option value="em-andamento">Em Andamento</option>
                                            <option value="pendente">Pendente</option>
                                            <option value="resolvido">Resolvido</option>
                                        </select>
                                        <select class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" v-model="priorityFilter">
                                            <option value="">Todas as Prioridades</option>
                                            <option value="baixa">Baixa</option>
                                            <option value="media">Média</option>
                                            <option value="alta">Alta</option>
                                            <option value="urgente">Urgente</option>
                                        </select>
                                    </div>
                                    
                                    <div class="flex items-center justify-between sm:justify-end space-x-2">
                                        <button class="hidden sm:flex bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 items-center space-x-2 text-sm">
                                            <Plus class="w-4 h-4" />
                                            <span>Novo Chamado</span>
                                        </button>
                                        <button class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                            <Filter class="w-5 h-5" />
                                        </button>
                                        <button class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                            <RefreshCw class="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Tickets List -->
                    <div class="flex-1 px-4 lg:px-6 pb-4 lg:pb-6 overflow-auto">
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
                            <div class="px-4 lg:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Chamados Recentes</h3>
                            </div>
                            <div class="divide-y divide-gray-200 dark:divide-gray-700">
                                <div 
                                    v-for="ticket in filteredTickets" 
                                    :key="ticket.id"
                                    class="p-4 lg:p-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    @click="selectTicket(ticket)"
                                >
                                    <!-- Mobile Layout -->
                                    <div class="block lg:hidden space-y-3">
                                        <div class="flex items-start justify-between">
                                            <div class="flex items-center space-x-3">
                                                <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                                    <span class="text-xs font-medium text-gray-600 dark:text-gray-200">#{{ ticket.id }}</span>
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ ticket.title }}</p>
                                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ ticket.requester }}</p>
                                                </div>
                                            </div>
                                            <div class="flex flex-col items-end space-y-1">
                                                <span 
                                                    :class="getPriorityClass(ticket.priority) + ' inline-flex items-center px-2 py-1 rounded-full text-xs font-medium'"
                                                >
                                                    {{ ticket.priority }}
                                                </span>
                                                <span 
                                                    :class="getStatusClass(ticket.status) + ' inline-flex items-center px-2 py-1 rounded-full text-xs font-medium'"
                                                >
                                                    {{ ticket.status }}
                                                </span>
                                            </div>
                                        </div>
                                        <p class="text-sm text-gray-500 dark:text-gray-300">{{ ticket.description }}</p>
                                        <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                            <span>{{ ticket.created }}</span>
                                            <span>{{ ticket.assignee }}</span>
                                        </div>
                                    </div>

                                    <!-- Desktop Layout -->
                                    <div class="hidden lg:flex items-center justify-between">
                                        <div class="flex items-center space-x-4">
                                            <div class="flex-shrink-0">
                                                <div class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                                    <span class="text-sm font-medium text-gray-600 dark:text-gray-200">#{{ ticket.id }}</span>
                                                </div>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ ticket.title }}</p>
                                                <p class="text-sm text-gray-500 dark:text-gray-300 truncate">{{ ticket.description }}</p>
                                                <div class="flex items-center mt-2 space-x-4">
                                                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ ticket.requester }}</span>
                                                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ ticket.created }}</span>
                                                    <span class="text-xs text-gray-500 dark:text-gray-400">Categoria: {{ ticket.category }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex items-center space-x-4">
                                            <span 
                                                :class="getPriorityClass(ticket.priority) + ' inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'"
                                            >
                                                {{ ticket.priority }}
                                            </span>
                                            <span 
                                                :class="getStatusClass(ticket.status) + ' inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'"
                                            >
                                                {{ ticket.status }}
                                            </span>
                                            <div class="flex items-center space-x-1">
                                                <div class="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                                    <span class="text-xs font-medium text-blue-600 dark:text-blue-300">{{ ticket.assignee.charAt(0) }}</span>
                                                </div>
                                                <span class="text-xs text-gray-500 dark:text-gray-400">{{ ticket.assignee }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </BaseTemplate>
</template>

<script setup>
import BaseTemplate from '../templates/BaseTemplate.vue';
import { 
    LayoutDashboard, 
    Ticket, 
    Clock, 
    AlertCircle, 
    CheckCircle, 
    Users, 
    Plus, 
    Search, 
    Filter, 
    RefreshCw,
    Headset,
    Menu,
    X
} from 'lucide-vue-next';
import { ref, computed } from 'vue';

const searchQuery = ref('');
const statusFilter = ref('');
const priorityFilter = ref('');
const sidebarOpen = ref(false);

const tickets = ref([
    {
        id: '001',
        title: 'Problema com impressora do 2º andar',
        description: 'Impressora HP LaserJet não está funcionando, apresenta erro de papel',
        requester: 'Maria Santos',
        assignee: 'João Silva',
        status: 'Em Andamento',
        priority: 'Média',
        category: 'Hardware',
        created: '2 horas atrás'
    },
    {
        id: '002',
        title: 'Acesso negado ao sistema ERP',
        description: 'Usuário não consegue acessar o sistema ERP após mudança de senha',
        requester: 'Carlos Oliveira',
        assignee: 'Ana Costa',
        status: 'Aberto',
        priority: 'Alta',
        category: 'Software',
        created: '1 hora atrás'
    },
    {
        id: '003',
        title: 'Computador não liga',
        description: 'Estação de trabalho da contabilidade não está ligando',
        requester: 'Pedro Lima',
        assignee: 'João Silva',
        status: 'Urgente',
        priority: 'Urgente',
        category: 'Hardware',
        created: '30 min atrás'
    },
    {
        id: '004',
        title: 'Email não está sincronizando',
        description: 'Outlook não está recebendo emails novos há 2 dias',
        requester: 'Lucia Ferreira',
        assignee: 'Roberto Dias',
        status: 'Pendente',
        priority: 'Baixa',
        category: 'Email',
        created: '3 horas atrás'
    },
    {
        id: '005',
        title: 'Instalação de software',
        description: 'Solicitação para instalação do Adobe Photoshop',
        requester: 'Design Team',
        assignee: 'Ana Costa',
        status: 'Resolvido',
        priority: 'Baixa',
        category: 'Software',
        created: '1 dia atrás'
    }
]);

const filteredTickets = computed(() => {
    return tickets.value.filter(ticket => {
        const matchesSearch = !searchQuery.value || 
            ticket.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            ticket.description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            ticket.requester.toLowerCase().includes(searchQuery.value.toLowerCase());
        
        const matchesStatus = !statusFilter.value || 
            ticket.status.toLowerCase().replace(' ', '-') === statusFilter.value;
        
        const matchesPriority = !priorityFilter.value || 
            ticket.priority.toLowerCase() === priorityFilter.value;
        
        return matchesSearch && matchesStatus && matchesPriority;
    });
});

const getPriorityClass = (priority) => {
    const classes = {
        'Baixa': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
        'Média': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        'Alta': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        'Urgente': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return classes[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

const getStatusClass = (status) => {
    const classes = {
        'Aberto': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        'Em Andamento': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        'Pendente': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        'Resolvido': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        'Urgente': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

const selectTicket = (ticket) => {
    console.log('Ticket selecionado:', ticket);
    // Aqui você pode implementar a navegação para detalhes do ticket
};
</script>
